import type { ExplorerPane } from 'types/explorer'

import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { GROUP } from '@disco/constants/ItemTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  DEFAULT_VIEW,
  PLAYLIST_VIEW,
  TRACK_VIEW,
  TRACKLESS_VIEWS,
} from '@disco/constants/ViewTypes'

/**
 * Generates explorer pane parameters based on search type and sorting preferences.
 *
 * Maps search types to their corresponding view types and applies the appropriate
 * sort parameters. Each content type (track, playlist, album, artist) has its
 * own view configuration and sort parameter mapping.
 *
 * @param params - Configuration object
 * @param params.type - Type of content being searched ('track', 'playlist', 'album', 'artist', 'channel', or false)
 * @param params.sort - Sort field to apply
 * @param params.sortDirection - Sort direction ('asc' or 'desc')
 * @returns Partial explorer pane configuration object
 *
 * @example
 * ```typescript
 * // For track search
 * const trackParams = getPaneParamsBasedOnSearchType({
 *   type: 'track',
 *   sort: 'title',
 *   sortDirection: 'asc'
 * })
 * // Returns: { view: 'track', trackSortParam: 'title', trackSortDirection: 'asc' }
 *
 * // For playlist search
 * const playlistParams = getPaneParamsBasedOnSearchType({
 *   type: 'playlist',
 *   sort: 'created',
 *   sortDirection: 'desc'
 * })
 * // Returns: { view: 'playlist', playlistSortParam: 'created', playlistSortDirection: 'desc' }
 *
 * // For no specific type
 * const defaultParams = getPaneParamsBasedOnSearchType({
 *   type: false,
 *   sort: 'title',
 *   sortDirection: 'asc'
 * })
 * // Returns: { view: 'default' }
 * ```
 */
export const getPaneParamsBasedOnSearchType = ({
  type,
  sort,
  sortDirection,
}: {
  type: false | 'track' | 'playlist' | 'album' | 'artist' | 'channel'
  sort: SORT
  sortDirection: SORT_DIRECTION
}): Partial<ExplorerPane> => {
  if (!type)
    return {
      view: DEFAULT_VIEW,
    }
  if (type === 'playlist')
    return {
      view: PLAYLIST_VIEW,
      playlistSortParam: sort,
      playlistSortDirection: sortDirection,
    }
  if (type === 'album')
    return {
      view: ALBUM_VIEW,
      playlistSortParam: sort,
      playlistSortDirection: sortDirection,
    }
  if (type === 'artist')
    return {
      view: ARTIST_VIEW,
      artistSortParam: sort,
      artistSortDirection: sortDirection,
    }
  if (type === 'track')
    return {
      view: TRACK_VIEW,
      trackSortParam: sort,
      trackSortDirection: sortDirection,
    }

  return {}
}

/**
 * Where new rows belong in a pane's items: after the folder rows a channel or
 * folder column lists first, before its tracks and playlists.
 *
 * Uploads in progress and the finished rows that replace them both use this, so
 * a row does not jump once its upload completes.
 *
 * @param items - The pane's items, folders first
 * @returns Index of the first non-folder item
 */
export const getPaneInsertIndexAfterFolders = (
  items: Array<{ type?: string }> | null | undefined,
): number => {
  const firstNonFolder = (items ?? []).findIndex(item => item.type !== GROUP)
  return firstNonFolder === -1 ? (items ?? []).length : firstNonFolder
}

/**
 * Whether a pane is an open channel/folder column that lists its group's
 * tracks directly. These are the panes `handleTracksChange` reconciles when
 * tracks change group membership (drag-move, upload, add-to-channel) — and
 * therefore the only panes that may show upload-in-progress rows: anywhere
 * else the row would never be replaced by its finished track row.
 *
 * Columns with an active inline search are excluded, since membership-based
 * insertion would add tracks that do not match the query (DISCO-7820).
 *
 * @param pane - The pane to test
 * @returns True when group-membership changes land as visible rows here
 */
export const isReconcilableGroupPane = (
  pane: Pick<
    ExplorerPane,
    'type' | 'groupId' | 'groupType' | 'searchTerm' | 'view'
  >,
): boolean => {
  const isTracklessView =
    pane.view !== undefined && TRACKLESS_VIEWS.includes(pane.view)
  // only custom channels list tracks in the default view
  const showsGroupTracks =
    pane.groupType === CUSTOM_CHANNEL ||
    (!!pane.view && pane.view !== DEFAULT_VIEW)
  return (
    pane.type === SEARCH_RESULTS_PANE &&
    !!pane.groupId &&
    !pane.searchTerm &&
    !isTracklessView &&
    showsGroupTracks
  )
}
